import {
  Text,
  Spinner,
  Badge,
  Button,
  GreyGrey30,
  GreyGrey80,
  GreyGrey90,
  IconArrowRight,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  ShadowXs,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { useNavigate } from 'react-router-dom'
import { formatDateRange, MINUTES } from '@sequencehq/utils/dates'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  toInvoicePaymentStatusBadgeProps,
  toInvoiceStatusBadgeProps
} from '@sequencehq/utils'
import { INVOICES_DEFAULT_SORT_BY } from 'components/Invoices/invoices.constants'
import { CUSTOMER_BILLING_TABLE_LIMIT } from 'Customer/components/tables/billingTables.constants'
import { useInvoiceDueDateFormatter } from 'Invoices/hooks/useInvoiceDueDateFormatter'
import { toAmountBasis, toListedInvoiceAmount } from 'Invoices/utils'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export const InvoiceTable = () => {
  const navigate = useNavigate()
  const { customer } = useCustomerContext()
  const formatDueDate = useInvoiceDueDateFormatter()
  const { useCreditGrantLedger } = useFeatureFlags()
  // The v99990101 list is the one that reports the credit a grant has applied.
  const invoicesQuery = useQuery(
    dashboardv99990101Client.getInvoices,
    {
      customerId: customer.id,
      limit: CUSTOMER_BILLING_TABLE_LIMIT,
      sortBy: 'ACCOUNTING_DATE',
      excludeInvoiceStatus: 'VOIDED'
    },
    { staleTime: 1 * MINUTES }
  )

  const invoices = invoicesQuery.data

  if (!invoices) {
    return (
      <Center height="200px" width="100%" mt={4}>
        <Spinner />
      </Center>
    )
  }
  return (
    <>
      <TableContainer
        borderRadius="lg"
        overflow="hidden"
        border={`1px solid ${GreyGrey30}`}
        mt={4}
        width="100%"
        boxShadow={ShadowXs}
      >
        <Table variant="v2" width="100%">
          <Thead>
            <Tr>
              <Th width={100} textAlign="right">
                Amount
              </Th>
              <Th width={140}>Status</Th>
              <Th width={100}>Invoice #</Th>
              <Th width={200}>Billing period</Th>
              <Th width={120}>Sent</Th>
              <Th width={100}>Due</Th>
              <Th width={100}>Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {invoices.items.length > 0 ? (
              invoices.items.map(invoice => {
                const dueDateText = formatDueDate({
                  dueDate: invoice.dueDate,
                  formatDate: dueDate =>
                    dateTimeWithFormat(dueDate, 'd MMM yyyy'),
                  fallback: '-'
                })

                return (
                  <Tr
                    key={invoice.id}
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                  >
                    <Td textAlign="right">
                      <Text
                        {...Primary13Regular}
                        as={Link.Unstyled}
                        to={`/invoices/${invoice.id}`}
                        color={IndigoIndigo60}
                        onClick={e => e.stopPropagation()}
                        data-testid={`customer.invoices.${invoice.id}.amount`}
                      >
                        {toListedInvoiceAmount({
                          invoice,
                          basis: toAmountBasis(useCreditGrantLedger)
                        })}
                      </Text>
                    </Td>
                    <Td>
                      <Flex gap={2} alignItems="center">
                        <Badge
                          {...toInvoiceStatusBadgeProps({
                            status: invoice.status,
                            size: 'sm'
                          })}
                        />
                        {invoice.status === 'SENT' && (
                          <Badge
                            {...toInvoicePaymentStatusBadgeProps({
                              status: invoice.paymentStatus,
                              size: 'sm'
                            })}
                          />
                        )}
                      </Flex>
                    </Td>
                    <Td>{invoice.invoiceNumber || '-'}</Td>
                    <Td>
                      {invoice.billingPeriod
                        ? formatDateRange({
                            from: new Date(invoice.billingPeriod.start),
                            to: new Date(invoice.billingPeriod.endInclusive)
                          })
                        : '-'}
                    </Td>
                    <Td>
                      {invoice.issueDate
                        ? dateTimeWithFormat(invoice.issueDate, 'd MMM yyyy')
                        : '-'}
                    </Td>
                    <Td data-testid={`customer.invoices.${invoice.id}.dueDate`}>
                      {dueDateText}
                    </Td>
                    <Td>
                      {invoice.accountingDate
                        ? dateTimeWithFormat(
                            invoice.accountingDate,
                            'd MMM yyyy'
                          )
                        : '-'}
                    </Td>
                  </Tr>
                )
              })
            ) : (
              <Tr
                sx={{
                  cursor: 'auto!important',
                  _hover: {
                    bgColor: 'inherit!important'
                  }
                }}
              >
                <Td colSpan={7}>
                  <EmptyContent />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Button
        {...Primary13SemiBold}
        style={{ marginTop: '8px' }}
        variant="ghost"
        trailingIcon={<IconArrowRight color={IndigoIndigo60} />}
        as={Link.Unstyled}
        to={`/invoices/?sortBy=${INVOICES_DEFAULT_SORT_BY}&customerId=${customer.id}`}
        data-testid="customer.invoices.viewAll"
      >
        View all invoices
      </Button>
    </>
  )
}

const EmptyContent = () => {
  return (
    <Box width="400px" margin="12px auto" textAlign="center">
      <Text {...Primary13SemiBold} color={GreyGrey90}>
        No invoices to display
      </Text>
      <Text {...Primary13Regular} color={GreyGrey80}>
        You have not created an invoice for this customer yet.
      </Text>
    </Box>
  )
}
